import React, { useState } from "react";

const TypeCheckboxes = ({ selectedTypes, handleTypeFilterChange }) => {
  const [showAbbreviations, setShowAbbreviations] = useState(false);

  // College types with full names and matching icons for better understanding
  const collegeTypes = [
    {
      code: "PVT",
      name: "Private",
      icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    },
    {
      code: "PU",
      name: "Public University",
      icon: "M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z",
    },
    {
      code: "SF",
      name: "Self-Financing",
      icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2z",
    },
    {
      code: "UNIV",
      name: "University",
      icon: "M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253",
    },
    {
      code: "SS",
      name: "State Sponsored",
      icon: "M11 11V9a2 2 0 00-2-2m2 4v4a2 2 0 104 0v-1m-4-3H9m2 0h4m6 1a9 9 0 11-18 0 9 9 0 0118 0z",
    },
  ];

  // Calculate number of types selected
  const activeCount = selectedTypes.length;

  return (
    <div className="h-full bg-white rounded-xl shadow-md border border-gray-100 p-4">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 -m-4 mb-3 px-4 py-3 border-b border-gray-100 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-blue-600 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <h3 className="font-bold text-gray-800 flex items-center">
              College Types
              <button
                onClick={() => setShowAbbreviations(!showAbbreviations)}
                className="ml-1 text-blue-500 hover:text-blue-700 focus:outline-none"
                title="Show abbreviation meanings"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </h3>

            {activeCount > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                {activeCount} active
              </span>
            )}
          </div>

          {activeCount > 0 && (
            <button
              onClick={() => {
                selectedTypes.forEach((type) => {
                  handleTypeFilterChange(type);
                });
              }}
              className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-1.5 py-0.5 rounded"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Abbreviations panel */}
      {showAbbreviations && (
        <div className="mb-3 p-2 text-xs bg-blue-50 rounded border border-blue-100 text-blue-700">
          <ul className="grid grid-cols-2 gap-1">
            {collegeTypes.map((type) => (
              <li key={type.code} className="flex items-start">
                <span className="font-bold">{type.code}:</span>
                <span className="ml-1">{type.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2">
        {collegeTypes.map((type) => {
          const isSelected = selectedTypes.includes(type.code);
          return (
            <button
              key={type.code}
              onClick={() => handleTypeFilterChange(type.code)}
              className={`px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-150 flex items-center ${
                isSelected
                  ? "bg-blue-100 text-blue-800 border border-blue-300"
                  : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
              }`}
              title={type.name}
            >
              <span
                className={`w-3.5 h-3.5 mr-1 rounded-sm flex items-center justify-center ${
                  isSelected ? "bg-blue-600" : "bg-white border border-gray-400"
                }`}
              >
                {isSelected && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-2.5 w-2.5 text-white"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </span>
              <svg
                className="w-3 h-3 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d={type.icon}
                />
              </svg>
              {type.code}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TypeCheckboxes;
